import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation, useOutlet, useSearchParams } from "react-router-dom";
import { Gallery } from "react-grid-gallery";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import arrow from "../../img/Arrow.svg";
import mask from "../../img/Logo Mask.svg";
import "./index.css";
import Link from "../Link";
import { EventData, events, useEvent } from "./utils";
import Space from "../Space";
import ReactPlayer from "react-player";
import Input from "../Input";
import Menu from "../Menu";

function EventCard({ id, name, location, date, cover, disabled }: EventData) {
  const content = (
    <>
      <img src={cover} className="EventCardCover" alt={`${name} Event Cover`} />
      <div className="EventCardContent">
        <div className="EventCardDetails">
          {location}, {date}
        </div>
        <h2 className="EventCardTitle">{name}</h2>
      </div>
    </>
  );

  if (disabled) {
    return <div className="EventCard">{content}</div>;
  }

  return (
    <Link variant="none" to={`/live/${id}`} className="EventCard">
      {content}
    </Link>
  );
}

export function EventsListScreen() {
  return (
    <>
      <Menu>
        <h1 className="EventsListTitle">Events</h1>
      </Menu>

      <div className="EventsListScreen">
        {events.map((event) => (
          <Fragment key={event.id}>
            <Space size="xl" />
            <EventCard {...event} />
          </Fragment>
        ))}
      </div>
    </>
  );
}

export function EventDetailScreen() {
  const event = useEvent();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [code, setCode] = useState<string>("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const unlocked = searchParams.get("code");
    setWidth(containerRef.current?.offsetWidth);

    if (unlocked) {
      setCode(unlocked.toUpperCase());
    }
  }, [searchParams]);

  const handleCode = (event: React.FormEvent<HTMLInputElement>) => {
    setCode(event.currentTarget.value.toUpperCase());
  };

  return (
    <>
      <Menu>
        <Link variant="none" to="/live" className="EventsBackButton">
          <img src={arrow} className="EventsArrow" alt="Arrow Back" />
        </Link>
      </Menu>

      <Space size="l" />

      {event && (
        <div className="EventDetailScreen" ref={containerRef}>
          <div className="EventDetails">
            {event.location}, {event.date}
          </div>
          <Space size="m" />
          <h2 className="EventTitle">{event.name}</h2>
          <Space size="xxl" />
          <p className="EventDescription">{event.description}</p>
          <Space size="xxl" />

          {event.video && width && (
            <div
              className="EventPlayer"
              style={{ width, height: width / event.video.ratio }}
            >
              {!event.video.code || code === event.video.code.toUpperCase() ? (
                <ReactPlayer
                  url={event.video.url}
                  playing
                  controls
                  width={width}
                  height={width / event.video.ratio}
                />
              ) : (
                <>
                  <Input
                    className="VideoCode"
                    placeholder="enter code to watch aftermovie"
                    onChange={handleCode}
                    value={code}
                  />
                  <img className="VideoCover" src={event.video.cover} alt={`${event.name} Video Cover`} />
                </>
              )}
            </div>
          )}

          <Space size="xl" />

          {event.gallery.length > 0 && (
            <Link variant="link" to="gallery">
              view photo gallery
            </Link>
          )}

          <Space size="m" />
          <Link variant="link" to="/merch">
            get merch
          </Link>

          <Space size="xxl" />
        </div>
      )}
    </>
  );
}

export function EventGalleryScreen() {
  const event = useEvent();
  const [index, setIndex] = useState(-1);
  const handleClick = (index: number) => setIndex(index);

  return (
    <>
      <Menu>
        <Link
          variant="none"
          to=".."
          relative="path"
          className="EventsBackButton"
        >
          <img src={arrow} className="EventsArrow" alt="Back Arrow" />
        </Link>
      </Menu>

      <Space size="l" />

      {event && (
        <>
          <Gallery
            images={event.gallery}
            enableImageSelection={false}
            onClick={handleClick}
          />
          <Lightbox
            slides={event.gallery}
            open={index >= 0}
            index={index}
            close={() => setIndex(-1)}
          />
        </>
      )}
    </>
  );
}

export default function EventsPage() {
  const location = useLocation();
  const nodeRef = useRef(null);
  const currentOutlef = useOutlet();

  return (
    <div className="EventsPage" data-pathname={location.pathname}>
      <SwitchTransition>
        <CSSTransition
          key={location.pathname}
          nodeRef={nodeRef}
          timeout={400}
          classNames="EventsContent"
          unmountOnExit
        >
          {() => (
            <main ref={nodeRef} className="EventsContent">
              {currentOutlef}
            </main>
          )}
        </CSSTransition>
      </SwitchTransition>

      <footer className="EventsFooter">
        <Link to="/live" variant="none">
          <img src={mask} className="EventsLogo" alt="Stockholm Noir Mask" />
        </Link>
      </footer>
    </div>
  );
}
